import React, { useMemo, useLayoutEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import { aggregateLeaderboardCharts } from './leaderboardReportChartsUtils';

const CHART_H = 280;
const STROKE = '#6366f1';
const BAR_FILL = '#14b8a6';

function dayBucketLabel(t, key) {
  const map = {
    '0-3': 'reports.leaderboardCharts.dayBucket_0_3',
    '4-7': 'reports.leaderboardCharts.dayBucket_4_7',
    '8-14': 'reports.leaderboardCharts.dayBucket_8_14',
    '15+': 'reports.leaderboardCharts.dayBucket_15plus',
    '?': 'reports.leaderboardCharts.dayBucket_unknown'
  };
  return t(map[key] || 'reports.leaderboardCharts.dayBucket_unknown');
}

const ReportLeaderboardCharts = ({ rows, t }) => {
  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => window.cancelAnimationFrame(id);
  }, [rows?.length]);

  const { lineData, byDays, scatterData } = useMemo(
    () => aggregateLeaderboardCharts(rows || []),
    [rows]
  );

  const dayBarData = useMemo(
    () =>
      (byDays || []).map((b) => ({
        ...b,
        label: dayBucketLabel(t, b.key)
      })),
    [byDays, t]
  );

  if (!rows || rows.length === 0) {
    return (
      <div className="mb-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
        {t('reports.dailyExercisesApi.chartsEmpty')}
      </div>
    );
  }

  const chartBox = 'rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm shadow-slate-900/[0.04]';
  const title = 'text-sm font-bold text-slate-800 mb-2';

  return (
    <div className="mb-6 space-y-4">
      <div>
        <h2 className="text-base font-bold text-slate-900">{t('reports.leaderboardCharts.sectionTitle')}</h2>
        <p className="mt-1 text-xs font-medium text-slate-500">{t('reports.leaderboardCharts.footnote')}</p>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className={chartBox}>
          <h3 className={title}>{t('reports.leaderboardCharts.chartLineTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <LineChart data={lineData} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="rank" tick={{ fontSize: 10 }} label={{ value: t('reports.rank'), position: 'insideBottom', offset: -4, fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} label={{ value: t('reports.totalScore'), angle: -90, position: 'insideLeft', fontSize: 10 }} />
                <Tooltip formatter={(v) => [v, t('reports.totalScore')]} labelFormatter={(r) => `${t('reports.rank')}: ${r}`} />
                <Line type="monotone" dataKey="score" stroke={STROKE} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.leaderboardCharts.chartDaysBucketsTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <BarChart data={dayBarData} margin={{ top: 8, right: 12, left: 4, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={58} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, t('reports.dailyExercisesApi.chartStudentCount')]} />
                <Bar dataKey="count" fill={BAR_FILL} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={chartBox}>
          <h3 className={title}>{t('reports.leaderboardCharts.chartScatterTitle')}</h3>
          <div className="w-full min-w-0" style={{ height: CHART_H }} dir="ltr">
            <ResponsiveContainer width="100%" height={CHART_H}>
              <ScatterChart margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis
                  type="number"
                  dataKey="timeSec"
                  name={t('reports.leaderboardCharts.scatterTimeSec')}
                  tick={{ fontSize: 10 }}
                />
                <YAxis type="number" dataKey="score" name={t('reports.totalScore')} tick={{ fontSize: 10 }} />
                <ZAxis range={[60, 60]} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, name) => [v, name]} />
                <Scatter name={t('reports.leaderboardCharts.scatterLegend')} data={scatterData} fill="#8b5cf6" isAnimationActive={false} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportLeaderboardCharts;
